import React from "react";
import {
	Box,
	Button,
	Chip,
	Container,
	Grid,
	Paper,
	Stack,
	Typography,
} from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import SensorsRoundedIcon from "@mui/icons-material/SensorsRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import TimelineRoundedIcon from "@mui/icons-material/TimelineRounded";
import { useNavigate } from "react-router-dom";

const featureCards = [
	{
		title: "Telemetria operativa",
		description:
			"Consulta el estado de medidores, conectividad y comportamiento del parque instalado en un solo tablero.",
		icon: <SensorsRoundedIcon />,
	},
	{
		title: "Lectura util para negocio",
		description:
			"Detecta excepciones, eventos y variaciones relevantes para actuar antes de afectar al usuario final.",
		icon: <TimelineRoundedIcon />,
	},
	{
		title: "Acceso por suministrador",
		description:
			"Cada permisionario administra sus dispositivos, usuarios, zonas y configuraciones dentro de su propio entorno.",
		icon: <SecurityRoundedIcon />,
	},
];

const stats = [
	{ value: "24/7", label: "Monitoreo continuo" },
	{ value: "1", label: "Vista centralizada" },
	{ value: "100%", label: "Foco operativo" },
];

export default function LandingScreen(): JSX.Element {
	document.title = "Inicio";
	const navigate = useNavigate();

	return (
		<Box
			sx={{
				minHeight: "100vh",
				background:
					"radial-gradient(circle at top left, rgba(43, 108, 176, 0.24), transparent 30%), radial-gradient(circle at 85% 15%, rgba(16, 185, 129, 0.18), transparent 22%), linear-gradient(135deg, #06131f 0%, #0c2032 45%, #eef4f7 45%, #f7f8f4 100%)",
				color: "#0f172a",
			}}>
			<Box
				component='header'
				sx={{
					position: "sticky",
					top: 0,
					zIndex: 10,
					backdropFilter: "blur(18px)",
					backgroundColor: "rgba(6, 19, 31, 0.62)",
					borderBottom: "1px solid rgba(255,255,255,0.08)",
				}}>
				<Container maxWidth='lg'>
					<Stack
						direction='row'
						alignItems='center'
						justifyContent='space-between'
						sx={{ py: 2 }}>
						<Stack direction='row' alignItems='center' spacing={1.5}>
							<Box
								sx={{
									width: 42,
									height: 42,
									borderRadius: "14px",
									display: "grid",
									placeItems: "center",
									background:
										"linear-gradient(135deg, #38bdf8 0%, #14b8a6 100%)",
									color: "#04111d",
									boxShadow: "0 16px 30px rgba(20, 184, 166, 0.24)",
								}}>
								<BoltRoundedIcon />
							</Box>
							<Box>
								<Typography
									sx={{
										fontSize: 12,
										letterSpacing: "0.28em",
										textTransform: "uppercase",
										color: "rgba(226, 232, 240, 0.72)",
									}}>
									Permissionaries
								</Typography>
								<Typography
									sx={{
										fontWeight: 700,
										color: "#f8fafc",
										letterSpacing: "-0.03em",
									}}>
									Plataforma para suministradores calificados
								</Typography>
							</Box>
						</Stack>

						<Button
							variant='contained'
							onClick={() => navigate("/login")}
							sx={{
								borderRadius: "999px",
								px: 3,
								py: 1.1,
								textTransform: "none",
								fontWeight: 700,
								background: "linear-gradient(135deg, #f8fafc 0%, #dbeafe 100%)",
								color: "#060606",
							}}>
							Iniciar sesion
						</Button>
					</Stack>
				</Container>
			</Box>

			<Container maxWidth='lg' sx={{ pt: { xs: 8, md: 12 }, pb: 10 }}>
				<Grid container spacing={{ xs: 6, md: 8 }} alignItems='center'>
					<Grid item xs={12} md={7}>
						<Box
							sx={{
								position: "relative",
								borderRadius: { xs: "28px", md: "36px" },
								p: { xs: 3, md: 4.5 },
								background:
									"linear-gradient(180deg, rgba(6, 19, 31, 0.92) 0%, rgba(7, 26, 43, 0.78) 100%)",
								border: "1px solid rgba(148, 163, 184, 0.14)",
								boxShadow: "0 28px 60px rgba(2, 6, 23, 0.22)",
								overflow: "hidden",
							}}>
							<Box
								sx={{
									position: "absolute",
									inset: 0,
									background:
										"radial-gradient(circle at top left, rgba(20, 184, 166, 0.16), transparent 26%), radial-gradient(circle at bottom left, rgba(14, 165, 233, 0.14), transparent 28%)",
								}}
							/>
							<Stack spacing={3} sx={{ position: "relative" }}>
								<Chip
									label='Infraestructura de medicion para el mercado electrico'
									sx={{
										alignSelf: "flex-start",
										backgroundColor: "rgba(15, 23, 42, 0.2)",
										color: "#dbeafe",
										border: "1px solid rgba(191, 219, 254, 0.22)",
									}}
								/>
								<Typography
									sx={{
										fontSize: { xs: "2.8rem", md: "4.6rem" },
										lineHeight: 0.96,
										fontWeight: 800,
										letterSpacing: "-0.05em",
										color: "#f8fafc",
										textShadow: "0 10px 30px rgba(2, 6, 23, 0.36)",
										maxWidth: 760,
									}}>
									Monitorea medidores, conectividad y eventos de tu operacion en
									un entorno confiable.
								</Typography>
								<Typography
									sx={{
										fontSize: { xs: "1.05rem", md: "1.2rem" },
										lineHeight: 1.7,
										color: "#dbe7f3",
										textShadow: "0 6px 24px rgba(2, 6, 23, 0.24)",
										maxWidth: 620,
									}}>
									Permissionaries centraliza la supervision de los dispositivos
									que instalan los permisionarios para que cada suministrador de
									servicios calificados opere con visibilidad, control y
									respuesta rapida.
								</Typography>

								<Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
									<Button
										variant='contained'
										endIcon={<ArrowForwardRoundedIcon />}
										onClick={() => navigate("/login")}
										sx={{
											borderRadius: "999px",
											px: 3.5,
											py: 1.4,
											textTransform: "none",
											fontWeight: 700,
											background:
												"linear-gradient(135deg, #14b8a6 0%, #0ea5e9 100%)",
											boxShadow: "0 20px 40px rgba(14, 165, 233, 0.24)",
										}}>
										Entrar a la plataforma
									</Button>
									<Button
										variant='outlined'
										onClick={() => navigate("/login")}
										sx={{
											borderRadius: "999px",
											px: 3.5,
											py: 1.4,
											textTransform: "none",
											fontWeight: 700,
											borderColor: "rgb(217, 217, 218)",
											color: { xs: "#d9d8d8", md: "#d1d1d2" },
										}}>
										Iniciar sesion
									</Button>
								</Stack>

								<Stack
									direction={{ xs: "column", sm: "row" }}
									spacing={2}
									sx={{ pt: 2 }}>
									{stats.map((stat) => (
										<Paper
											key={stat.label}
											elevation={0}
											sx={{
												minWidth: 140,
												px: 2.5,
												py: 2,
												borderRadius: "20px",
												backgroundColor: "rgba(248, 250, 252, 0.12)",
												border: "1px solid rgba(255,255,255,0.12)",
												backdropFilter: "blur(12px)",
											}}>
											<Typography
												sx={{
													fontSize: "1.8rem",
													fontWeight: 800,
													color: "#f8fafc",
												}}>
												{stat.value}
											</Typography>
											<Typography
												sx={{
													fontSize: 14,
													color: "rgba(226, 232, 240, 0.82)",
												}}>
												{stat.label}
											</Typography>
										</Paper>
									))}
								</Stack>
							</Stack>
						</Box>
					</Grid>

					<Grid item xs={12} md={5}>
						<Paper
							elevation={0}
							sx={{
								position: "relative",
								overflow: "hidden",
								borderRadius: "30px",
								p: { xs: 3, md: 4 },
								background:
									"linear-gradient(180deg, rgba(7, 18, 31, 0.94) 0%, rgba(15, 23, 42, 0.92) 100%)",
								border: "1px solid rgba(148, 163, 184, 0.18)",
								boxShadow: "0 28px 60px rgba(2, 6, 23, 0.28)",
							}}>
							<Box
								sx={{
									position: "absolute",
									inset: 0,
									background:
										"radial-gradient(circle at top right, rgba(34, 211, 238, 0.18), transparent 32%)",
								}}
							/>
							<Stack spacing={2.5} sx={{ position: "relative" }}>
								<Typography
									sx={{
										fontSize: 12,
										letterSpacing: "0.24em",
										textTransform: "uppercase",
										color: "rgba(148, 163, 184, 0.8)",
									}}>
									Operacion supervisada
								</Typography>
								<Typography
									sx={{
										fontSize: "2rem",
										lineHeight: 1.05,
										fontWeight: 800,
										color: "#f8fafc",
									}}>
									Una vista clara para cada activo instalado.
								</Typography>
								<Stack spacing={2}>
									{featureCards.map((feature) => (
										<Box
											key={feature.title}
											sx={{
												p: 2.2,
												borderRadius: "22px",
												backgroundColor: "rgba(15, 23, 42, 0.64)",
												border: "1px solid rgba(148, 163, 184, 0.16)",
											}}>
											<Stack
												direction='row'
												spacing={2}
												alignItems='flex-start'>
												<Box
													sx={{
														width: 44,
														height: 44,
														borderRadius: "14px",
														display: "grid",
														placeItems: "center",
														background:
															"linear-gradient(135deg, rgba(20, 184, 166, 0.24), rgba(56, 189, 248, 0.24))",
														color: "#67e8f9",
														flexShrink: 0,
													}}>
													{feature.icon}
												</Box>
												<Box>
													<Typography
														sx={{
															fontWeight: 700,
															color: "#f8fafc",
															mb: 0.8,
														}}>
														{feature.title}
													</Typography>
													<Typography
														sx={{
															fontSize: 14,
															lineHeight: 1.7,
															color: "rgba(226, 232, 240, 0.74)",
														}}>
														{feature.description}
													</Typography>
												</Box>
											</Stack>
										</Box>
									))}
								</Stack>
							</Stack>
						</Paper>
					</Grid>
				</Grid>
			</Container>
		</Box>
	);
}
